import * as React from "react"
import { ChevronRight, Loader2, TriangleAlert, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { getErrorMessage } from "@/lib/errors"
import { toneClass } from "@/lib/status"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet"
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges"

/*
  SidePanel — the right-side form drawer (kit `.drawer`): header (title, subtitle, status pill,
  actions, ×), a scrollable body of sections, and a footer that always stays visible
  (Cancel / Save with a saving state and the save error). Full screen on phones.

    const [draft, setDraft] = useState(initial);
    const dirty = !isEqual(draft, initial);
    <SidePanel open={open} onOpenChange={setOpen} width="md"
      title="45-416MS00F" subtitle="Local Control Panel IP42" status={<StatusPill status="Active" />}
      actions={<RowActions items={…} />}
      dirty={dirty}
      onSave={async () => { await api.put(`/catalog/${id}`, draft); notify.success('Item saved'); }}>
      <SidePanelSection title="Where it is used">…</SidePanelSection>
      <SidePanelSection title="Details">…fields…</SidePanelSection>
      <SidePanelSection title="More" collapsible defaultOpen={false}>…</SidePanelSection>
    </SidePanel>

  - Unsaved edits are guarded: ×, Cancel, Escape and a click outside ask "Leave without saving?" first
    while `dirty`; leaving the page (links, reload) is guarded too (useUnsavedChanges).
  - onSave may be async: the button shows "Saving…"; a thrown error stays in the footer (as readable
    text — getErrorMessage) and the panel stays open; on success the panel closes (closeOnSave).
    Validate in onSave (throw an Error with a readable message) — there is no native form validation.
  - The panel is not a <form>: buttons in the body never submit, and content with its own <form> keeps
    working. Enter in a single-line field of the body saves (not in textareas, check boxes, an open
    autocomplete, a field inside its own <form>, or when a handler called preventDefault()). A custom
    `footer` calls its own handlers (type="submit" does nothing here).
  Props: open, onOpenChange, title, subtitle, status (node next to the title), actions (header node),
  icon + iconTone (tile before the title), width ('sm' 400 | 'md' 460 | 'lg' 640 | 'xl' 800),
  dirty (boolean or count), onSave, saveLabel, cancelLabel, saving / error (controlled), saveDisabled,
  closeOnSave (true), footerStart (left side of the footer, e.g. "Delete…"), footer (replaces the whole
  footer), hideFooter, modal (true; false = a pane beside the list), closeOnOutsideClick (true),
  confirmOptions (useUnsavedChanges texts), bodyClassName, className, testId ('side-panel'),
  saveTestId / cancelTestId (test ids of the default footer buttons; default `<testId>-save` /
  `<testId>-cancel` — for a page that must keep a legacy id such as submit-lpo-btn).
*/

const WIDTHS = {
  sm: "sm:w-[400px]",
  md: "sm:w-[460px]",
  lg: "sm:w-[640px]",
  xl: "sm:w-[800px]",
}

// The input types where Enter submits a native form (single-line fields — not check boxes, radios,
// buttons, files or textareas).
const ENTER_SAVES_TYPES = new Set([
  "text", "search", "email", "url", "tel", "password", "number",
  "date", "month", "week", "time", "datetime-local",
])

/** Enter in a single-line field of the panel body saves — like a form, without being a form. */
function isEnterToSave(event) {
  if (event.key !== "Enter" || event.defaultPrevented) return false
  if (event.nativeEvent?.isComposing || event.keyCode === 229) return false // IME composition
  const target = event.target
  // React events bubble through portals: a popover or select opened from the body is not "in" it.
  if (!event.currentTarget.contains(target)) return false
  if (!target || target.tagName !== "INPUT") return false
  if (!ENTER_SAVES_TYPES.has((target.getAttribute("type") || "text").toLowerCase())) return false
  if (target.form) return false // content with its own <form> handles its own Enter
  if (target.getAttribute("aria-expanded") === "true") return false // an open autocomplete picks with Enter
  return true
}

function SidePanel({
  open,
  onOpenChange,
  title,
  subtitle,
  status,
  actions,
  icon: Icon,
  iconTone = "navy",
  width = "md",
  dirty = false,
  onSave,
  saveLabel = "Save changes",
  cancelLabel = "Cancel",
  saving: savingProp,
  error: errorProp,
  saveDisabled = false,
  closeOnSave = true,
  footerStart,
  footer,
  hideFooter = false,
  modal = true,
  closeOnOutsideClick = true,
  confirmOptions,
  bodyClassName,
  className,
  children,
  testId = "side-panel",
  saveTestId,
  cancelTestId,
}) {
  const isDirty = typeof dirty === "number" ? dirty > 0 : Boolean(dirty)
  const { confirmLeave, markClean } = useUnsavedChanges(Boolean(open) && isDirty, confirmOptions)
  const [savingState, setSaving] = React.useState(false)
  const [errorState, setError] = React.useState(null)
  const saving = savingProp ?? savingState
  const error = errorProp ?? errorState
  const closingRef = React.useRef(false)

  React.useEffect(() => {
    if (open) setError(null)
  }, [open])

  const requestClose = React.useCallback(async () => {
    if (saving || closingRef.current) return
    if (isDirty) {
      closingRef.current = true
      const ok = await confirmLeave()
      closingRef.current = false
      if (!ok) return
    }
    onOpenChange?.(false)
  }, [saving, isDirty, confirmLeave, onOpenChange])

  const save = async () => {
    if (!onSave || saving) return
    setError(null)
    setSaving(true)
    try {
      await onSave()
      markClean()
      if (closeOnSave) onOpenChange?.(false)
    } catch (err) {
      setError(getErrorMessage(err, "Could not save. Try again."))
    } finally {
      setSaving(false)
    }
  }

  const dirtyText = typeof dirty === "number" && dirty > 0
    ? `${dirty} unsaved ${dirty === 1 ? "change" : "changes"}`
    : "Unsaved changes"

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? onOpenChange?.(true) : requestClose())} modal={modal}>
      <SheetContent
        side="right"
        hideClose
        overlayClassName="bg-[color:var(--scrim)]"
        onInteractOutside={(event) => { if (!closeOnOutsideClick || !modal) event.preventDefault() }}
        className={cn(
          "flex w-full flex-col gap-0 border-l border-line bg-surface p-0 text-ink shadow-overlay sm:max-w-[calc(100vw-2rem)]",
          WIDTHS[width] || WIDTHS.md,
          className
        )}
        data-testid={testId}
      >
        <header className="flex items-start gap-3 border-b border-line-2 px-4 py-4 sm:px-[22px] sm:py-[18px]">
          {Icon ? (
            <span className={cn("ui-icon-tile h-9 w-9", toneClass(iconTone))} aria-hidden="true">
              <Icon className="h-[18px] w-[18px]" />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <SheetTitle className="min-w-0 truncate font-heading text-[17px] font-extrabold leading-tight tracking-[-0.01em] text-ink">
                {title}
              </SheetTitle>
              {status}
            </div>
            {subtitle ? (
              <SheetDescription className="mt-1 text-[13px] text-ink-3">{subtitle}</SheetDescription>
            ) : (
              <SheetDescription className="sr-only">{typeof title === "string" ? title : "Details"}</SheetDescription>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {actions}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={requestClose}
              aria-label="Close"
              title="Close (Esc)"
              className="text-ink-3"
              data-testid={`${testId}-close`}
            >
              <X aria-hidden="true" />
            </Button>
          </div>
        </header>

        {/* Not a <form>: body buttons stay plain buttons (no accidental submit), and content that brings
            its own <form> is not nested in another one. Enter in a single-line field saves instead. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn("flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-4 sm:px-[22px] sm:py-[18px]", bodyClassName)}
            onKeyDown={(event) => {
              if (onSave && !saveDisabled && isEnterToSave(event)) {
                event.preventDefault()
                save()
              }
            }}
            data-testid={`${testId}-body`}
          >
            {children}
          </div>

          {hideFooter ? null : footer !== undefined ? footer : (
            <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {error ? (
                  <span className="flex min-w-0 items-center gap-1.5 text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid={`${testId}-error`}>
                    <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0">{error}</span>
                  </span>
                ) : isDirty ? (
                  <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-3" data-testid={`${testId}-dirty`}>
                    <span className="ui-tone-dot tone-amber h-2 w-2" aria-hidden="true" />
                    {dirtyText}
                  </span>
                ) : null}
                {footerStart}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" variant="ghost" onClick={requestClose} disabled={saving} data-testid={cancelTestId || `${testId}-cancel`}>
                  {cancelLabel}
                </Button>
                {onSave ? (
                  <Button type="button" onClick={save} disabled={saving || saveDisabled} data-testid={saveTestId || `${testId}-save`}>
                    {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                    {saving ? "Saving…" : error ? "Try again" : saveLabel}
                  </Button>
                ) : null}
              </div>
            </footer>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/*
  SidePanelSection — a titled block in the panel body. `collapsible` sections start open unless
  defaultOpen={false} (the "More" details that are rarely needed). `actions` sit right of the title.
*/
function SidePanelSection({ title, description, actions, collapsible = false, defaultOpen = true, className, children }) {
  const [open, setOpen] = React.useState(defaultOpen)
  const contentId = React.useId()
  const heading = collapsible ? (
    <button
      type="button"
      onClick={() => setOpen((value) => !value)}
      aria-expanded={open}
      aria-controls={contentId}
      className="-ml-1 flex items-center gap-1 rounded px-1 text-left hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} aria-hidden="true" />
      {title}
    </button>
  ) : title
  return (
    <section className={cn("flex flex-col gap-3", className)}>
      {title || actions ? (
        <div className="flex items-center gap-2">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{heading}</h3>
          {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
        </div>
      ) : null}
      {description ? <p className="-mt-1.5 text-[12.5px] text-ink-3">{description}</p> : null}
      {!collapsible || open ? <div id={contentId} className="flex flex-col gap-3">{children}</div> : null}
    </section>
  )
}

export { SidePanel, SidePanelSection }
